import React, { useEffect, useMemo, useRef, useState } from "react";
import { db } from "../firebase";
import { useLocation, Link } from "react-router-dom";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  where,
  setDoc,
  limit,
  serverTimestamp,
  onSnapshot
} from "firebase/firestore";
import {
  Calendar,
  Monitor,
  Trash2,
  Cpu,
  Info,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  UserRound,
  Mail,
  Phone,
  ExternalLink,
  Copy,
  UserPlus,
} from "lucide-react";
import { createPortal } from "react-dom";
import AdminHeader from "../components/AdminHeader";
import AdminSidebar from "../components/AdminSidebar";

/* =========================
   Config / Constants
========================= */
const NEON = "#D4FF00";
const PCS = ["PC1", "PC2", "PC3", "PC4", "PC5"];
// keep rigs inside viewBox 0..60
const RIG_POS = {
  PC1: { x: 15, y: 25 },
  PC2: { x: 50, y: 18 },
  PC3: { x: 85, y: 25 },
  PC4: { x: 30, y: 48 },
  PC5: { x: 70, y: 48 },
};

/* =========================
   Helper: ensure /users doc exists for a booking email (returns patch)
========================= */
async function ensureUserForBooking(db, booking) {
  const email = (booking.userEmail || booking.guestEmail || "").trim();
  const name = booking.userName || booking.guestName || "Player";
  const phone = booking.userPhone || booking.guestPhone || null;

  if (!email) return null;

  // 1) find user by email
  const qUsers = query(collection(db, "users"), where("email", "==", email), limit(1));
  const snap = await getDocs(qUsers);

  let userId;
  let userRef;

  if (!snap.empty) {
    // reuse existing
    userRef = snap.docs[0].ref;
    userId = snap.docs[0].id;
    await setDoc(
      userRef,
      {
        displayName: name,
        email,
        phone: phone || null,
        lastBookingAt: serverTimestamp(),
      },
      { merge: true }
    );
  } else {
    // create new
    userRef = doc(collection(db, "users")); // auto id
    userId = userRef.id;
    await setDoc(userRef, {
      displayName: name,
      email,
      phone: phone || null,
      createdAt: serverTimestamp(),
      lastBookingAt: serverTimestamp(),
      source: "booking_auto",
    });
  }

  // 3) patch booking and return the fields we changed
  const patch = {
    userId,
    userEmail: email,
    userName: name,
    userPhone: phone || null,
  };
  await setDoc(doc(db, "bookings", booking.id), patch, { merge: true });
  return patch;
}

/* =========================
   Admin Page
========================= */
export default function AdminBookingMap() {
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [bookings, setBookings] = useState([]);
  const [selected, setSelected] = useState(null); // single booking
  const [selectedRig, setSelectedRig] = useState(null); // PC1..PC5
  const [toast, setToast] = useState("");

  // prevent re-running ensureUserForBooking repeatedly for same booking
  const reconciledRef = useRef(new Set()); // booking IDs processed for current date

  // parallax bg (optional aesthetic)
  const stageRef = useRef(null);
  useEffect(() => {
    const el = stageRef.current;
    const onMove = (e) => {
      if (!el) return;
      const x = (e.clientX - window.innerWidth / 2) / window.innerWidth;
      const y = (e.clientY - window.innerHeight / 2) / window.innerHeight;
      el.style.setProperty("--mx", x);
      el.style.setProperty("--my", y);
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  // 🔴 REPLACED old load() with live subscription (primary + fallback)
  useEffect(() => {
    // reset processed set when date changes
    reconciledRef.current = new Set();

    const qPrimary = query(
      collection(db, "bookings"),
      where("date", "==", date),
      orderBy("startTime", "asc")
    );
    const qFallback = query(collection(db, "bookings"), where("date", "==", date));

    let unsubFallback = null;

    const handleSnapshot = (snap, alreadySorted) => {
      let items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      if (!alreadySorted) {
        items.sort((a, b) => (a.startTime || "").localeCompare(b.startTime || ""));
      }

      setBookings(items);

      // keep selection alive if still present
      setSelected((prev) => (prev ? items.find((b) => b.id === prev.id) || null : null));
      // keep selectedRig as-is; the Rig panel uses byRig derived below

      // reconcile users once per booking id
      (async () => {
        for (const b of items) {
          const email = b.userEmail || b.guestEmail;
          if (!b.userId && email && !reconciledRef.current.has(b.id)) {
            try {
              reconciledRef.current.add(b.id);
              const patch = await ensureUserForBooking(db, b);
              if (patch) {
                // optimistic local update (snapshot will reflect soon anyway)
                setBookings((prev) => prev.map((x) => (x.id === b.id ? { ...x, ...patch } : x)));
                setSelected((prev) => (prev && prev.id === b.id ? { ...prev, ...patch } : prev));
              }
            } catch (err) {
              // allow retry later by removing flag
              reconciledRef.current.delete(b.id);
              console.warn("ensureUserForBooking failed:", err);
            }
          }
        }
      })();
    };

    const unsubPrimary = onSnapshot(
      qPrimary,
      (snap) => handleSnapshot(snap, true),
      (err) => {
        console.warn("Primary query failed (likely missing index). Falling back.", err);
        unsubFallback = onSnapshot(
          qFallback,
          (snap) => handleSnapshot(snap, false),
          (err2) => console.error("Fallback query failed:", err2)
        );
      }
    );

    return () => {
      if (typeof unsubPrimary === "function") unsubPrimary();
      if (typeof unsubFallback === "function") unsubFallback();
    };
  }, [db, date]);

  const byRig = useMemo(() => {
    const map = {};
    PCS.forEach((pc) => (map[pc] = []));
    bookings.forEach((b) => {
      if (!map[b.pcId]) map[b.pcId] = [];
      map[b.pcId].push(b);
    });
    return map;
  }, [bookings]);

  const bookedRigs = useMemo(() => new Set(bookings.map((b) => b.pcId)), [bookings]);

  async function cancelBooking(id) {
    try {
      await deleteDoc(doc(db, "bookings", id));
      setToast("✅ Booking canceled.");
      setBookings((prev) => prev.filter((b) => b.id !== id));
      if (selected?.id === id) setSelected(null);
      // keep selectedRig as-is; rig panel will update automatically from byRig
    } catch (e) {
      console.error(e);
      setToast("❌ Failed to cancel booking.");
    } finally {
      setTimeout(() => setToast(""), 2200);
    }
  }

  // Manual attach/create for a specific booking (optimistic state update)
  async function attachSelectedUser(target) {
    if (!target) return;
    try {
      const patch = await ensureUserForBooking(db, target);
      if (patch) {
        setBookings((prev) => prev.map((b) => (b.id === target.id ? { ...b, ...patch } : b)));
        setSelected((prev) => (prev && prev.id === target.id ? { ...prev, ...patch } : prev));
        setToast("✅ User attached/created.");
      } else {
        setToast("⚠️ No email on booking to attach.");
      }
    } catch (e) {
      console.error(e);
      setToast("❌ Couldn’t attach/create user.");
    } finally {
      setTimeout(() => setToast(""), 2000);
    }
  }

  // Date helpers
  function toISO(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
  }
  function shiftDay(n) {
    const d = new Date(date + "T00:00:00");
    d.setDate(d.getDate() + n);
    setDate(toISO(d));
  }

  // Copy helper
  async function copy(text) {
    try {
      await navigator.clipboard.writeText(text);
      setToast("✅ Copied to clipboard");
      setTimeout(() => setToast(""), 1400);
    } catch {}
  }

  const navItems = [
    { name: "Site Config", path: "/admin" },
    { name: "User Manager", path: "/admin/users" },
    { name: "Bookings", path: "/admin/bookings" },
    { name: "Revenue", path: "/admin/revenue" }
  ];
  const location = useLocation();

  return (
    <>
      <AdminHeader />
      <div className="flex flex-col md:flex-row">
        {/* Sidebar */}
        <div className="w-full md:w-48 bg-black border-b md:border-b-0 p-4">
          <AdminSidebar />
        </div>

        <main className="relative mx-auto lg:min-w-[1200px] min-h-[100dvh] bg-black text-white overflow-hidden">
          {/* Parallax neon grid */}
          <div ref={stageRef} className="absolute inset-0 -z-10" style={{ perspective: "1200px" }}>
            <div
              className="absolute -inset-[8%]"
              style={{
                transform:
                  "translate3d(calc(var(--mx,0) * 20px), calc(var(--my,0) * 20px), 0)",
              }}
            >
              <div
                className="absolute inset-0 opacity-20"
                style={{
                  backgroundImage:
                    "linear-gradient(90deg, rgba(255,255,255,0.07) 1px, transparent 1px), linear-gradient(rgba(255,255,255,0.07) 1px, transparent 1px)",
                  backgroundSize: "60px 60px",
                  filter: "drop-shadow(0 0 10px rgba(212,255,0,.06))",
                }}
              />
              <div
                className="absolute inset-0"
                style={{
                  background:
                    "radial-gradient(60% 60% at 50% 20%, rgba(212,255,0,.08), transparent 45%), radial-gradient(40% 30% at 80% 80%, rgba(212,255,0,.06), transparent 50%)",
                }}
              />
            </div>
          </div>

          <div className="relative z-10 max-w-6xl mx-auto px-4 py-10 space-y-8">
            {/* Header */}
            <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
              <div>
                <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#a9ffa2]/70">
                  <Monitor className="w-4 h-4" />
                  <span>RGC // Admin Ops Console</span>
                </div>
                <h1 className="mt-2 text-3xl md:text-5xl font-extrabold">
                  Booking <span className="text-[--neon]">Control</span> Room
                </h1>
              </div>

              {/* Fancy date controls */}
              <div className="flex items-center gap-3">
                <button
                  onClick={() => shiftDay(-1)}
                  className="p-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10"
                  title="Previous day"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>

                <div className="w-72">
                  <NeonDatePicker value={date} onChange={setDate} />
                </div>

                <button
                  onClick={() => shiftDay(1)}
                  className="p-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10"
                  title="Next day"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </header>

            {/* Selected booking OR rig panel (ABOVE floor plan) */}
            <section className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl p-5 md:p-6 shadow-[0_0_40px_rgba(212,255,0,.08)]">
              <div className="mb-4 flex items-center gap-2">
                <Info className="w-4 h-4 text-[--neon]" />
                <h2 className="text-lg font-bold">
                  {selected
                    ? "Booking details"
                    : selectedRig
                    ? `Bookings on ${selectedRig}`
                    : "Booking details"}
                </h2>
              </div>

              {!selected && !selectedRig ? (
                <div className="text-neutral-400 text-sm">
                  Select a booking from the list, tap a rig’s time pill, or click a rig to see all its bookings.
                </div>
              ) : selected ? (
                <BookingDetailCard
                  booking={selected}
                  onCancel={() => cancelBooking(selected.id)}
                  onCopy={copy}
                  onAttachUser={() => attachSelectedUser(selected)}
                />
              ) : (
                <RigBookingsPanel
                  pcId={selectedRig}
                  items={(byRig[selectedRig] || []).slice().sort((a, b) =>
                    (a.startTime || "").localeCompare(b.startTime || "")
                  )}
                  onOpen={(b) => {
                    setSelected(b);
                    setSelectedRig(null);
                  }}
                  onCancel={(b) => cancelBooking(b.id)}
                  onAttach={(b) => attachSelectedUser(b)}
                  onCopy={(id) => copy(id)}
                />
              )}
            </section>

            {/* Map + legend (SHORTER floor) */}
            <section className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl p-5 md:p-6 shadow-[0_0_40px_rgba(212,255,0,.08)]">
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-black/60 border border-white/10">
                    <Cpu className="w-4 h-4 text-[--neon]" />
                  </span>
                  <div>
                    <div className="text-sm text-neutral-300 font-semibold tracking-wide">
                      Café Floor Plan — {date}
                    </div>
                    <div className="text-xs text-neutral-500">Click a rig to see all its bookings, or a time pill for a single booking</div>
                  </div>
                </div>
                <Legend />
              </div>

              <div className="relative overflow-visible rounded-xl border border-white/10">
                <AdminFloorSVG
                  byRig={byRig}
                  bookedRigs={bookedRigs}
                  onSelectBooking={(b) => {
                    setSelected(b);
                    setSelectedRig(null);
                  }}
                  onSelectRig={(pc) => {
                    setSelected(null);
                    setSelectedRig(pc);
                  }}
                />
                <div className="pointer-events-none absolute inset-0 mix-blend-screen opacity-[0.07] bg-[linear-gradient(rgba(255,255,255,.35)_1px,transparent_1px)] bg-[length:100%_3px]" />
              </div>
            </section>

            {/* List + cancel (shows guest OR user info) */}
            <section className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl p-5 md:p-6 shadow-[0_0_40px_rgba(212,255,0,.08)]">
              <div className="mb-4 flex items-center gap-2">
                <Info className="w-4 h-4 text-[--neon]" />
                <h2 className="text-lg font-bold">Bookings — {date}</h2>
              </div>

              <div className="overflow-x-auto rounded-xl border border-white/10">
                <table className="w-full text-sm">
                  <thead className="bg-black/50 text-neutral-400">
                    <tr>
                      <th className="p-3 text-left">PC</th>
                      <th className="p-3 text-left">Time</th>
                      <th className="p-3 text-left">Name</th>
                      <th className="p-3 text-left">Email</th>
                      <th className="p-3 text-left">Phone</th>
                      <th className="p-3 text-left">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.length === 0 ? (
                      <tr>
                        <td className="p-4 text-neutral-500" colSpan={6}>
                          No bookings.
                        </td>
                      </tr>
                    ) : (
                      bookings.map((b) => {
                        const name = b.userName || b.guestName || "—";
                        const email = b.userEmail || b.guestEmail || "—";
                        const phone = b.userPhone || b.guestPhone || "—";
                        const needsAttach = !b.userId && email && email !== "—";
                        return (
                          <tr
                            key={b.id}
                            className={`border-t border-white/10 hover:bg-white/5 cursor-pointer ${
                              selected?.id === b.id ? "bg-white/10" : ""
                            }`}
                            onClick={() => {
                              setSelected(b);
                              setSelectedRig(null);
                            }}
                          >
                            <td className="p-3">{b.pcId}</td>
                            <td className="p-3">
                              {b.startTime} – {b.endTime}
                            </td>
                            <td className="p-3">{name}</td>
                            <td className="p-3">{email}</td>
                            <td className="p-3">{phone}</td>
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                {needsAttach && (
                                  <button
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      await attachSelectedUser(b);
                                    }}
                                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs border border-white/10 hover:border-[--neon]/60"
                                    title="Attach/Create user"
                                  >
                                    <UserPlus className="w-3.5 h-3.5" />
                                    Attach
                                  </button>
                                )}
                                <a
                                  href={email !== "—" ? `mailto:${email}` : undefined}
                                  onClick={(e) => email === "—" && e.preventDefault()}
                                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs border ${
                                    email === "—"
                                      ? "opacity-40 cursor-not-allowed border-white/10"
                                      : "border-white/10 hover:border-[--neon]/60"
                                  }`}
                                  title="Email"
                                >
                                  <Mail className="w-3.5 h-3.5" /> Email
                                </a>
                                <a
                                  href={phone !== "—" ? `tel:${phone}` : undefined}
                                  onClick={(e) => phone === "—" && e.preventDefault()}
                                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs border ${
                                    phone === "—"
                                      ? "opacity-40 cursor-not-allowed border-white/10"
                                      : "border-white/10 hover:border-[--neon]/60"
                                  }`}
                                  title="Call"
                                >
                                  <Phone className="w-3.5 h-3.5" /> Call
                                </a>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    cancelBooking(b.id);
                                  }}
                                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs bg-red-600 hover:bg-red-700 text-white"
                                  title="Cancel"
                                >
                                  <Trash2 className="w-3.5 h-3.5" /> Cancel
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {toast && (
                <div
                  className={`mt-3 flex items-center gap-2 px-4 py-3 rounded-lg text-sm ${
                    toast.startsWith("✅")
                      ? "bg-green-900/40 text-green-300"
                      : "bg-red-900/40 text-red-300"
                  }`}
                >
                  {toast.startsWith("✅") ? <CheckCircle2 className="w-5 h-5" /> : null}
                  {toast}
                </div>
              )}
            </section>
          </div>

          <style>{`
            :root { --neon: ${NEON}; }
          `}</style>
        </main>
      </div>
    </>
  );
}

/* =========================
   Rig Bookings Panel (lists ALL bookings for a PC)
========================= */
function RigBookingsPanel({ pcId, items, onOpen, onCancel, onAttach, onCopy }) {
  if (!items || items.length === 0) {
    return (
      <div className="text-neutral-400 text-sm">
        No bookings on <span className="text-white font-semibold">{pcId}</span> for this date.
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <div className="text-sm text-neutral-300">
        <span className="text-white font-semibold">{pcId}</span> — {items.length} booking
        {items.length > 1 ? "s" : ""}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {items.map((b) => {
          const name = b.userName || b.guestName || "—";
          const email = b.userEmail || b.guestEmail || "—";
          const phone = b.userPhone || b.guestPhone || "—";
          const needsAttach = !b.userId && email && email !== "—";
          return (
            <div
              key={b.id}
              className="rounded-xl border border-white/10 bg-black/40 p-3 hover:border-[--neon]/40 transition"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-semibold text-white">
                  {b.startTime} – {b.endTime}
                </div>
                <button
                  onClick={() => onCopy(b.id)}
                  className="text-xs px-2 py-1 rounded border border-white/10 hover:border-[--neon]/60"
                  title="Copy booking ID"
                >
                  <Copy className="w-3.5 h-3.5 inline-block mr-1" />
                  ID
                </button>
              </div>
              <div className="mt-2 text-sm">
                <div className="text-neutral-400 text-xs uppercase tracking-wider">Name</div>
                <div className="font-medium">{name}</div>
              </div>
              <div className="mt-1 text-sm grid grid-cols-2 gap-2">
                <div>
                  <div className="text-neutral-400 text-xs uppercase tracking-wider">Email</div>
                  <div className="truncate">
                    {email !== "—" ? (
                      <a href={`mailto:${email}`} className="hover:underline">
                        {email}
                      </a>
                    ) : (
                      "—"
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-neutral-400 text-xs uppercase tracking-wider">Phone</div>
                  <div>
                    {phone !== "—" ? (
                      <a href={`tel:${phone}`} className="hover:underline">
                        {phone}
                      </a>
                    ) : (
                      "—"
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button
                  onClick={() => onOpen(b)}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs border border-white/10 hover:border-[--neon]/60"
                  title="Open full details"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  Open
                </button>
                {needsAttach && (
                  <button
                    onClick={() => onAttach(b)}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs border border-white/10 hover:border-[--neon]/60"
                    title="Attach/Create user"
                  >
                    <UserPlus className="w-3.5 h-3.5" />
                    Attach
                  </button>
                )}
                <button
                  onClick={() => onCancel(b)}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs bg-red-600 hover:bg-red-700 text-white"
                  title="Cancel"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Cancel
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* =========================
   Booking Detail Card (single)
========================= */
function BookingDetailCard({ booking, onCancel, onCopy, onAttachUser }) {
  const {
    id,
    pcId,
    date,
    startTime,
    endTime,
    userId,
    userName,
    userEmail,
    userPhone,
    guestName,
    guestEmail,
    guestPhone,
  } = booking;

  const name = userName || guestName || "—";
  const email = userEmail || guestEmail || "—";
  const phone = userPhone || guestPhone || "—";
  const needsAttach = !userId && email && email !== "—";

  return (
    <div className="grid gap-4 md:grid-cols-3">
      <div className="md:col-span-2">
        <div className="grid gap-3 sm:grid-cols-2">
          <InfoRow label="PC" value={pcId} />
          <InfoRow label="Date" value={date} />
          <InfoRow label="Window" value={`${startTime} – ${endTime}`} />
          <InfoRow
            label="User"
            value={
              userId ? (
                <a
                  href={`/admin/users/${userId}`}
                  className="inline-flex items-center gap-1 text-[--neon] hover:underline"
                >
                  <UserRound className="w-4 h-4" />
                  {name}
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              ) : (
                name
              )
            }
          />
          <InfoRow
            label="Email"
            value={
              email !== "—" ? (
                <a href={`mailto:${email}`} className="hover:underline">
                  {email}
                </a>
              ) : (
                "—"
              )
            }
          />
          <InfoRow
            label="Phone"
            value={
              phone !== "—" ? (
                <a href={`tel:${phone}`} className="hover:underline">
                  {phone}
                </a>
              ) : (
                "—"
              )
            }
          />
          <InfoRow
            label="Booking ID"
            value={
              <span className="inline-flex items-center gap-2">
                <code className="bg-black/40 px-1.5 py-0.5 rounded border border-white/10 text-xs">
                  {id}
                </code>
                <button
                  onClick={() => onCopy(id)}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-xs border border-white/10 hover:border-[--neon]/60"
                  title="Copy ID"
                >
                  <Copy className="w-3.5 h-3.5" />
                  Copy
                </button>
              </span>
            }
          />
        </div>
      </div>

      {/* Quick actions */}
      <div className="flex flex-col gap-2">
        {needsAttach && (
          <button
            onClick={onAttachUser}
            className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-white/10 hover:border-[--neon]/60"
            title="Create or attach user by email"
          >
            <UserPlus className="w-4 h-4" />
            Attach/Create user
          </button>
        )}
        <button
          onClick={onCancel}
          className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white font-semibold"
        >
          <Trash2 className="w-4 h-4" />
          Cancel booking
        </button>
        {email !== "—" && (
          <a
            href={`mailto:${email}`}
            className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-white/10 hover:border-[--neon]/60"
          >
            <Mail className="w-4 h-4" />
            Email user
          </a>
        )}
        {phone !== "—" && (
          <a
            href={`tel:${phone}`}
            className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-white/10 hover:border-[--neon]/60"
          >
            <Phone className="w-4 h-4" />
            Call user
          </a>
        )}
        {booking.userId && (
          <a
            href={`/admin/users/${booking.userId}`}
            className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-white/10 hover:border-[--neon]/60"
          >
            <UserRound className="w-4 h-4" />
            Open user profile
          </a>
        )}
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="text-sm">
      <div className="text-[11px] uppercase tracking-wider text-neutral-400 mb-1">{label}</div>
      <div className="font-semibold text-white">{value}</div>
    </div>
  );
}

/* =========================
   Legend
========================= */
function Legend() {
  return (
    <div className="flex items-center gap-3 text-xs text-neutral-400">
      <Badge color="bg-[--neon]" label="No bookings" />
      <Badge color="bg-red-500" label="Booked" />
    </div>
  );
}
function Badge({ color, label }) {
  return (
    <span className="inline-flex items-center gap-2 px-2 py-1 rounded-full bg-black/50 border border-white/10">
      <span className={`inline-block w-2.5 h-2.5 rounded-full ${color}`} />
      {label}
    </span>
  );
}

/* =========================
   SVG Floor (SHORTER)
========================= */
function AdminFloorSVG({ byRig, bookedRigs, onSelectBooking, onSelectRig }) {
  return (
    <svg viewBox="0 0 100 60" className="w-full h-[260px] md:h-[320px] block bg-black/60">
      {/* walls */}
      <rect
        x="1"
        y="1"
        width="98"
        height="58"
        rx="2"
        fill="url(#g0)"
        stroke="rgba(255,255,255,.12)"
        strokeWidth="0.6"
      />
      {/* bar / desk zones */}
      <rect
        x="4"
        y="42"
        width="92"
        height="14"
        rx="1.5"
        fill="rgba(255,255,255,.03)"
        stroke="rgba(255,255,255,.08)"
        strokeWidth="0.4"
      />
      <rect
        x="4"
        y="4"
        width="92"
        height="10"
        rx="1.5"
        fill="rgba(255,255,255,.03)"
        stroke="rgba(255,255,255,.08)"
        strokeWidth="0.4"
      />

      {/* rigs */}
      {PCS.map((pc) => {
        const p = RIG_POS[pc];
        const isBooked = bookedRigs.has(pc);
        const pods = byRig[pc] || [];

        return (
          <g key={pc} transform={`translate(${p.x}, ${p.y})`}>
            {/* ring */}
            <circle
              cx="0"
              cy="0"
              r="7.4"
              fill="none"
              stroke={isBooked ? "rgba(239,68,68,1)" : NEON}
              strokeWidth="0.6"
              style={{
                filter: `drop-shadow(0 0 4px ${
                  isBooked ? "rgba(239,68,68,.8)" : "rgba(212,255,0,.8)"
                })`,
              }}
              onClick={() => onSelectRig(pc)}
              className="cursor-pointer"
            />
            {/* pod */}
            <circle
              cx="0"
              cy="0"
              r="6"
              fill={isBooked ? "rgba(239,68,68,.25)" : "rgba(212,255,0,.22)"}
              stroke={isBooked ? "rgba(239,68,68,.9)" : "rgba(212,255,0,.9)"}
              strokeWidth="0.6"
              onClick={() => onSelectRig(pc)}
              className="cursor-pointer"
            />
            {/* icon */}
            <Monitor x={-3.7} y={-3.7} width={7.4} height={7.4} color="white" opacity="0.9" />
            {/* label */}
            <text
              x="0"
              y="11.2"
              textAnchor="middle"
              fontSize="3"
              fill="rgba(255,255,255,.9)"
              style={{ fontWeight: 700 }}
            >
              {pc}
            </text>

            {/* time pills under the pod (clickable) */}
            {pods.slice(0, 3).map((b, idx) => (
              <TimePill
                key={b.id}
                y={14 + idx * 5}
                start={b.startTime}
                end={b.endTime}
                onClick={() => onSelectBooking(b)}
              />
            ))}
            {pods.length > 3 && (
              <text
                x="0"
                y={14 + 3 * 5}
                textAnchor="middle"
                fontSize="2.2"
                fill="rgba(255,255,255,.7)"
              >
                +{pods.length - 3} more…
              </text>
            )}
          </g>
        );
      })}

      {/* defs */}
      <defs>
        <linearGradient id="g0" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0%" stopColor="rgba(255,255,255,.04)" />
          <stop offset="100%" stopColor="rgba(255,255,255,.01)" />
        </linearGradient>
      </defs>
    </svg>
  );
}

function TimePill({ y, start, end, onClick }) {
  return (
    <g transform={`translate(0, ${y})`} onClick={onClick} className="cursor-pointer">
      <rect
        x="-14"
        y="-3"
        width="28"
        height="6"
        rx="2"
        fill="rgba(0,0,0,.55)"
        stroke="rgba(255,255,255,.12)"
        strokeWidth="0.3"
      />
      <text x="0" y="1.7" textAnchor="middle" fontSize="2.2" fill="rgba(255,255,255,.9)">
        {start}–{end}
      </text>
    </g>
  );
}

/* ---------- Neon Date Picker (portal + outside click) ---------- */
function NeonDatePicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const popRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });

  // helpers (local so it's drop-in)
  const today = new Date();
  function toISO(dateObj) {
    const y = dateObj.getFullYear();
    const m = String(dateObj.getMonth() + 1).padStart(2, "0");
    const d = String(dateObj.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  function addDays(dateObj, n) {
    const d = new Date(dateObj);
    d.setDate(d.getDate() + n);
    return d;
  }

  const [view, setView] = useState(
    value ? new Date(value + "T00:00:00") : new Date(today.getFullYear(), today.getMonth(), 1)
  );
  const startOfMonth = new Date(view.getFullYear(), view.getMonth(), 1);
  const endOfMonth = new Date(view.getFullYear(), view.getMonth() + 1, 0);
  const startWeekday = (startOfMonth.getDay() + 6) % 7; // Monday=0
  const days = endOfMonth.getDate();

  const grid = [];
  for (let i = 0; i < startWeekday; i++) grid.push(null);
  for (let d = 1; d <= days; d++) grid.push(new Date(view.getFullYear(), view.getMonth(), d));

  function pick(dateObj) {
    onChange(toISO(dateObj));
    setOpen(false);
  }

  const weekendStart = (() => {
    const d = new Date(today);
       const delta = (6 - d.getDay() + 7) % 7; // next Saturday
      return addDays(d, delta);
  })();

  // position popover under the trigger
  function computePos() {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const width = 288; // ~ w-72
    const margin = 8;
    const left = Math.min(Math.max(8, rect.left), window.innerWidth - width - 8);
    const top = rect.bottom + margin;
    setPos({ top, left, width });
  }

  useEffect(() => {
    if (!open) return;
    computePos();
    const onScroll = () => computePos();
    const onResize = () => computePos();
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative">
      {/* Trigger */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full rounded-lg bg-black/60 border border-white/10 p-2 text-left text-white focus:ring-2 focus:ring-[--neon] outline-none flex items-center justify-between"
      >
        <span>{value || "Select date"}</span>
        <Calendar className="w-4 h-4 text-[--neon]" />
      </button>

      {/* Portal popover + overlay */}
      {open &&
        createPortal(
          <>
            {/* overlay: click anywhere to close */}
            <div className="fixed inset-0 z-[9999] bg-black/0" onMouseDown={() => setOpen(false)} />
            {/* popover */}
            <div
              ref={popRef}
              className="fixed z-[10000] w-72 rounded-xl border border-white/10 bg-black/80 backdrop-blur-xl shadow-[0_0_30px_rgba(212,255,0,.15)] p-3"
              style={{ top: pos.top, left: pos.left, width: pos.width || undefined }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-2">
                <button
                  onClick={() => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1))}
                  className="px-2 py-1 rounded bg-white/5 border border-white/10 text-neutral-300 hover:bg-white/10"
                >
                  ‹
                </button>
                <div className="text-sm font-semibold">
                  {view.toLocaleString(undefined, { month: "long", year: "numeric" })}
                </div>
                <button
                  onClick={() => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))}
                  className="px-2 py-1 rounded bg-white/5 border border-white/10 text-neutral-300 hover:bg-white/10"
                >
                  ›
                </button>
              </div>

              {/* Weekdays */}
              <div className="grid grid-cols-7 text-[10px] uppercase tracking-wider text-neutral-400 mb-1">
                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((w) => (
                  <div key={w} className="text-center py-1">
                    {w}
                  </div>
                ))}
              </div>

              {/* Days grid */}
              <div className="grid grid-cols-7 gap-1">
                {grid.map((d, i) => {
                  const todayFloor = new Date(
                    today.getFullYear(),
                    today.getMonth(),
                    today.getDate()
                  );
                  const isDisabled = !d || d < todayFloor;
                  const isSelected = d && value === toISO(d);
                  return (
                    <button
                      key={i}
                      disabled={!d || isDisabled}
                      onClick={() => d && pick(d)}
                      className={`aspect-square rounded-lg text-sm border transition
                        ${
                          !d
                            ? "invisible"
                            : isDisabled
                            ? "text-neutral-600 border-transparent"
                            : isSelected
                            ? "bg-[--neon] text-black font-bold border-[--neon]"
                            : "bg-white/5 text-neutral-200 border-white/10 hover:border-[--neon]/60"
                        }`}
                    >
                      {d ? d.getDate() : ""}
                    </button>
                  );
                })}
              </div>

              {/* Quick picks */}
              <div className="mt-3 flex items-center gap-2">
                <button
                  onClick={() => pick(new Date())}
                  className="text-xs px-2 py-1 rounded bg-white/5 border border-white/10 hover:border-[--neon]/60"
                >
                  Today
                </button>
                <button
                  onClick={() => pick(addDays(new Date(), 1))}
                  className="text-xs px-2 py-1 rounded bg-white/5 border border-white/10 hover:border-[--neon]/60"
                >
                  Tomorrow
                </button>
                <button
                  onClick={() =>
                    pick((() => {
                      const d = new Date();
                      const delta = (6 - d.getDay() + 7) % 7; // next Saturday
                      return addDays(d, delta);
                    })())
                  }
                  className="text-xs px-2 py-1 rounded bg-white/5 border border-white/10 hover:border-[--neon]/60"
                >
                  Weekend
                </button>
              </div>
            </div>
          </>,
          document.body
        )}
    </div>
  );
}

function Chip({ children, onClick }) {
  return (
    <button
      onClick={onClick}
      className="text-xs px-2 py-1 rounded-full bg-white/5 border border-white/10 hover:border-[--neon]/60"
    >
      {children}
    </button>
  );
}
